import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import notify, { MESSAGES } from '../../../utils/notify';
import { confirmDelete as askDelete } from '../../../utils/confirm';
import classnames from 'classnames';
import axios from 'axios';
import { api } from '../../../config';
import { getAuthToken, isAuthenticated } from '../../../auth/tokenvalidation';
import {
  formatDateToDDMMYYYY,
  formatDateToddmmyyhhmm,
} from '../../../utils/dateformatter';
import ActionButton from '../../../Components/Common/ActionButton';
import { EmptyState } from '../../../Components/Common/ListStates';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListFilterSelect,
  ListSearch,
} from '../../../Components/Common/ListFilters';
import {
  CONTRACT_TYPE_MAP,
  PROCUREMENT_TYPE_MAP,
  STEP_DEFS,
  buildListUrl,
  fetchJson,
  getLastUpdate,
  isReadyToSubmit,
  getQuickFilter,
  exportProcurementList,
} from '../utils/procurementList';
import { ContractBadge, DeadlineChip, ProductAvatar } from './ListBadges';
import StatusBadge from '../../../Components/Common/StatusBadge';
import { getListProductTypeLabel } from '../../../shared/offer-domain/constants';

// `filter` is the IsProcurementCompleted query value ('' = no filter).
const STAT_CARDS = [
  {
    key: 'total',
    filter: '',
    label: 'Total Procurement',
    icon: 'ri-stack-line',
    tone: 'total',
  },
  {
    key: 'draft',
    filter: 'false',
    label: 'Draft',
    icon: 'ri-draft-line',
    tone: 'progress',
  },
  {
    key: 'submitted',
    filter: 'true',
    label: 'Submitted',
    icon: 'ri-send-plane-line',
    tone: 'done',
  },
];

const STATUS_OPTIONS = [
  { value: 'false', label: 'Draft' },
  { value: 'true', label: 'Submitted' },
];

// Both are accepted by GET /procurement as ContractType / ProcurementType.
const CONTRACT_TYPE_OPTIONS = Object.entries(CONTRACT_TYPE_MAP).map(
  ([value, { label }]) => ({ value, label })
);
const PROCUREMENT_TYPE_OPTIONS = Object.entries(PROCUREMENT_TYPE_MAP).map(
  ([value, label]) => ({ value, label })
);

// Column id -> SortBy value understood by GET /procurement.
const SORT_FIELDS = {
  procurementnumber: 'procurementnumber',
  titleprocurement: 'titleprocurement',
  description: 'description',
  updatedDate: 'updateddate',
};

// Highlights drafts whose steps are all done, so they stand out for submit.
const READY_ROW_STYLES = [
  { when: row => isReadyToSubmit(row), classNames: ['pv-row-ready'] },
];

const formatRelativeTime = value => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '-';

  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDateToDDMMYYYY(date);
};

const getInitials = name => {
  const parts = String(name || '')
    .split('@')[0]
    .split(/[\s._-]+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  return parts
    .slice(0, 2)
    .map(part => part[0].toUpperCase())
    .join('');
};

const StatusPill = ({ row }) => {
  if (row?.isProcurementCompleted) {
    return <StatusBadge tone='success' label='Submitted' />;
  }
  if (isReadyToSubmit(row)) {
    return (
      <StatusBadge
        tone='success'
        label='Ready'
        className='pv-status-ready'
        title='All steps are complete. Ready to submit.'
      />
    );
  }
  return <StatusBadge tone='warning' label='Draft' className='pv-calm' />;
};

const LastUpdate = ({ row }) => {
  const value = getLastUpdate(row);
  const actor = row?.updatedBy || row?.createdBy;

  return (
    <span className='pv-update' title={formatDateToddmmyyhhmm(value)}>
      {actor && (
        <span className='pv-avatar' title={actor} aria-label={actor}>
          {getInitials(actor)}
        </span>
      )}
      <span>{formatRelativeTime(value)}</span>
    </span>
  );
};

const ProductInfo = ({ item }) => {
  const name = item?.productType?.product?.name;
  const sub = getListProductTypeLabel(item, item?.productType?.description);
  if (!name && !sub) return null;

  return (
    <div className='to-product'>
      <span className='to-product-name'>{name || '-'}</span>
      {sub && <span className='text-truncate ms-2'>· {sub}</span>}
    </div>
  );
};

const CompletionSteps = ({ item }) => {
  const doneCount = STEP_DEFS.filter(step => item?.[step.key] === true).length;
  const isReady = isReadyToSubmit(item);

  return (
    <div className='to-steps'>
      <div className='to-steps-track'>
        {STEP_DEFS.map((step, index) => {
          const isDone = item?.[step.key] === true;
          const state = `${step.label}: ${isDone ? 'Done' : 'Open'}`;

          return (
            <React.Fragment key={step.key}>
              {index > 0 && (
                <span
                  className={classnames('to-step-link', {
                    'is-done': item?.[STEP_DEFS[index - 1].key] === true,
                  })}
                />
              )}
              <span
                className={classnames('to-step', { 'is-done': isDone })}
                title={state}
                aria-label={state}
              >
                <i
                  className={isDone ? 'ri-check-line' : step.icon}
                  aria-hidden='true'
                />
              </span>
            </React.Fragment>
          );
        })}
      </div>
      <div className='to-steps-meta'>
        {isReady ? (
          <span className='pv-ready-text'>
            <i className='ri-rocket-2-line me-1' aria-hidden='true' />
            Ready to submit
          </span>
        ) : (
          <>
            <span>
              <strong>{doneCount}</strong>/{STEP_DEFS.length} steps
            </span>
            <span>{Math.round((doneCount / STEP_DEFS.length) * 100)}%</span>
          </>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ card, summary, isActive, onSelect }) => {
  const total = summary?.total || 0;
  const count = summary?.[card.key];
  const share =
    card.key !== 'total' && total > 0 && typeof count === 'number'
      ? Math.round((count / total) * 100)
      : null;

  return (
    <button
      type='button'
      className={classnames('to-stat', `to-stat--${card.tone}`, {
        'is-active': isActive,
      })}
      aria-pressed={isActive}
      onClick={onSelect}
    >
      <span className='to-stat-icon'>
        <i className={card.icon} aria-hidden='true' />
      </span>
      <span>
        <span className='to-stat-label'>{card.label}</span>
        <span className='to-stat-value'>
          {typeof count === 'number' ? count : '–'}
        </span>
      </span>
      {share !== null && <span className='to-stat-share'>{share}%</span>}
    </button>
  );
};

const SkeletonRows = ({ count = 5 }) => (
  <div className='w-100 px-3 py-2'>
    {Array.from({ length: count }).map((_, index) => (
      <div key={index} className='d-flex align-items-center gap-3 py-3'>
        <div className='to-skeleton' style={{ width: '9%' }} />
        <div className='to-skeleton' style={{ width: '26%' }} />
        <div className='to-skeleton' style={{ width: '12%' }} />
        <div className='to-skeleton' style={{ width: '18%' }} />
        <div className='to-skeleton flex-grow-1' />
      </div>
    ))}
  </div>
);

const ProcurementTable = ({
  procurementTypeHeader,
  refreshTrigger = 0,
  quickFilter = '',
  onQuickFilterChange,
  onDataChanged,
  onCreate,
  exportName = 'List',
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [completedFilter, setCompletedFilter] = useState('');
  const [contractTypeFilter, setContractTypeFilter] = useState('');
  const [procurementTypeFilter, setProcurementTypeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [sortField, setSortField] = useState('createddate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  // Bumped after a delete when no parent handles onDataChanged.
  const [reloadKey, setReloadKey] = useState(0);
  const activeQuickFilter = getQuickFilter(quickFilter);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    setCurrentPage(1);
  }, [quickFilter]);

  useEffect(() => {
    const token = getAuthToken();
    if (!token || !isAuthenticated()) {
      notify.error(MESSAGES.authRequired);
      return undefined;
    }

    const controller = new AbortController();
    setIsLoading(true);

    fetchJson(
      buildListUrl({
        currentPage,
        rowsPerPage,
        procurementTypeHeader,
        search: debouncedSearchText,
        completedFilter,
        quickFilter,
        contractType: contractTypeFilter,
        procurementType: procurementTypeFilter,
        sortField,
        sortDirection,
      }),
      token,
      controller.signal
    )
      .then(result => {
        setItems(result?.data?.items || []);
        setTotalRows(result?.data?.totalItems || 0);
      })
      .catch(error => {
        if (error.name === 'AbortError') return;
        notify.apiError(error, 'load procurement data');
        setItems([]);
        setTotalRows(0);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [
    currentPage,
    rowsPerPage,
    debouncedSearchText,
    completedFilter,
    quickFilter,
    contractTypeFilter,
    procurementTypeFilter,
    sortField,
    sortDirection,
    procurementTypeHeader,
    refreshTrigger,
    reloadKey,
  ]);

  // Counts come from the list endpoint with PageSize=1, reading totalItems.
  useEffect(() => {
    const token = getAuthToken();
    if (!token || !isAuthenticated()) return undefined;

    const controller = new AbortController();

    Promise.all(
      STAT_CARDS.map(async card => {
        const result = await fetchJson(
          buildListUrl({
            currentPage: 1,
            rowsPerPage: 1,
            procurementTypeHeader,
            completedFilter: card.filter,
          }),
          token,
          controller.signal
        );
        return [card.key, result?.data?.totalItems || 0];
      })
    )
      .then(entries => setSummary(Object.fromEntries(entries)))
      .catch(error => {
        // The list request already reports load failures; cards just hide counts.
        if (error.name !== 'AbortError') setSummary(null);
      });

    return () => controller.abort();
  }, [procurementTypeHeader, refreshTrigger, reloadKey]);

  const handleStatusFilter = value => {
    setCompletedFilter(value);
    setCurrentPage(1);
  };

  const handleContractTypeFilter = value => {
    setContractTypeFilter(value);
    setCurrentPage(1);
  };

  const handleProcurementTypeFilter = value => {
    setProcurementTypeFilter(value);
    setCurrentPage(1);
  };

  const handleSort = useCallback((column, direction) => {
    setSortField(SORT_FIELDS[column.id] || 'createddate');
    setSortDirection(direction);
    setCurrentPage(1);
  }, []);

  const handlePerRowsChange = newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchText('');
    setCompletedFilter('');
    setContractTypeFilter('');
    setProcurementTypeFilter('');
    setCurrentPage(1);
    onQuickFilterChange?.('');
  };

  // Exports every row matching the current filters, not just this page.
  const handleExport = async format => {
    const token = getAuthToken();
    if (!token || !isAuthenticated()) {
      notify.error(MESSAGES.authRequired);
      return;
    }

    setIsExporting(true);
    try {
      const query = {
        currentPage: 1,
        procurementTypeHeader,
        search: debouncedSearchText,
        completedFilter,
        quickFilter,
        contractType: contractTypeFilter,
        procurementType: procurementTypeFilter,
        sortField,
        sortDirection,
      };
      let result = await fetchJson(
        buildListUrl({ ...query, rowsPerPage: Math.max(totalRows, 1) }),
        token
      );
      const total = result?.data?.totalItems || 0;
      if (total > (result?.data?.items?.length || 0)) {
        result = await fetchJson(
          buildListUrl({ ...query, rowsPerPage: total }),
          token
        );
      }

      const rows = result?.data?.items || [];
      if (!rows.length) {
        notify.info(MESSAGES.noDataToExport);
        return;
      }
      exportProcurementList(rows, { format, sheetName: exportName });
    } catch (error) {
      notify.apiError(error, 'export procurement data');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDelete = async id => {
    try {
      setIsLoading(true);
      await axios.delete(`${api.API_URL}/procurement/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
      });
      notify.success('Data deleted successfully.');
    } catch (error) {
      notify.apiError(error, 'delete data');
    } finally {
      if (onDataChanged) onDataChanged();
      else setReloadKey(prev => prev + 1);
    }
  };

  const confirmDelete = async row => {
    const subject = [row.procurementNumber, row.titleProcurement]
      .filter(Boolean)
      .join(' - ');
    if (!(await askDelete('procurement', subject || undefined))) return;
    await handleDelete(row.id);
  };

  const goToDetail = row => navigate(`/apps-procurement/upd/${row.id}`);

  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const filterChips = [
    searchText && {
      key: 'search',
      label: 'Search',
      value: searchText,
      onRemove: () => setSearchText(''),
    },
    completedFilter && {
      key: 'status',
      label: 'Status',
      value:
        STATUS_OPTIONS.find(o => o.value === completedFilter)?.label ??
        completedFilter,
      onRemove: () => handleStatusFilter(''),
    },
    procurementTypeFilter && {
      key: 'procurementType',
      label: 'Proc Type',
      value:
        PROCUREMENT_TYPE_MAP[procurementTypeFilter] ?? procurementTypeFilter,
      onRemove: () => handleProcurementTypeFilter(''),
    },
    contractTypeFilter && {
      key: 'contractType',
      label: 'Period',
      value: CONTRACT_TYPE_MAP[contractTypeFilter]?.label ?? contractTypeFilter,
      onRemove: () => handleContractTypeFilter(''),
    },
    activeQuickFilter && {
      key: 'quick',
      label: 'Highlight',
      value: `Draft ${activeQuickFilter.label}`,
      onRemove: () => onQuickFilterChange?.(''),
    },
  ].filter(Boolean);
  const hasFilters = filterChips.length > 0;
  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  const emptyState = (
    <EmptyState
      title='No procurement found'
      emptyHint='No procurements have been created yet. Create one to start planning requirements and supplier invitations.'
      filteredHint='Nothing matches the current search or filters.'
      hasFilters={hasFilters}
      onClear={clearFilters}
      action={onCreate ? { label: 'Create New', onClick: onCreate } : undefined}
    />
  );

  const columns = [
    {
      id: 'procurementnumber',
      name: 'ID Proc',
      selector: row => row.procurementNumber,
      cell: row => (
        <span className='to-id'>{row.procurementNumber || '-'}</span>
      ),
      width: '9%',
      sortable: true,
    },
    {
      id: 'titleprocurement',
      name: 'Procurement',
      selector: row => row.titleProcurement,
      cell: row => (
        <div className='d-flex align-items-center gap-2 py-2 w-100'>
          <ProductAvatar name={row.productType?.product?.name} />
          <div className='pv-text-col flex-grow-1'>
            <div className='to-title' title={row.titleProcurement}>
              {row.titleProcurement || '-'}
            </div>
            <ProductInfo item={row} />
          </div>
        </div>
      ),
      width: '21%',
      sortable: true,
    },
    {
      id: 'procurementType',
      name: 'Proc Type',
      cell: row => (
        <div className='d-flex flex-column align-items-start gap-1'>
          <span className='to-tag'>
            {PROCUREMENT_TYPE_MAP[row.procurementType] || '-'}
          </span>
          <ContractBadge contractType={row.contractType} />
        </div>
      ),
      width: '10%',
    },
    {
      id: 'description',
      name: 'Description',
      selector: row => row.description,
      cell: row => (
        <span className='pv-desc' title={row.description || ''}>
          {row.description || '-'}
        </span>
      ),
      width: '12%',
      sortable: true,
    },
    {
      id: 'deadline',
      name: 'Deadline',
      cell: row => (
        <DeadlineChip
          deadline={row.proposalSubmissionDeadline}
          isClosed={row.isProcurementCompleted}
        />
      ),
      width: '11%',
    },
    {
      id: 'updatedDate',
      name: 'Last Update',
      selector: row => getLastUpdate(row),
      cell: row => <LastUpdate row={row} />,
      width: '10%',
      sortable: true,
    },
    {
      id: 'progress',
      name: 'Completion',
      cell: row => <CompletionSteps item={row} />,
      width: '13%',
    },
    {
      id: 'status',
      name: 'Status',
      cell: row => <StatusPill row={row} />,
      width: '8%',
    },
    {
      name: <span className='text-center w-100'>Action</span>,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center w-100'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-arrow-right-up-line'
            label='Open detail'
            onClick={e => {
              e.stopPropagation();
              goToDetail(row);
            }}
          />
          {!row.isProcurementCompleted && (
            <ActionButton
              variant='icon'
              size='sm'
              danger
              icon='ri-delete-bin-line'
              label='Delete procurement'
              confirm={false}
              onClick={e => {
                e.stopPropagation();
                confirmDelete(row);
              }}
            />
          )}
        </div>
      ),
      width: '6%',
      center: true,
    },
  ];

  const MobileCard = ({ item }) => (
    <div
      className={classnames(
        'to-mcard d-md-none',
        item.isProcurementCompleted || isReadyToSubmit(item)
          ? 'to-mcard--success'
          : 'to-mcard--warning',
        { 'pv-mcard-ready': isReadyToSubmit(item) }
      )}
    >
      <div className='d-flex justify-content-between align-items-center gap-2 mb-2'>
        <span className='d-inline-flex align-items-center gap-2'>
          <span className='to-id'>{item.procurementNumber || '-'}</span>
          <ContractBadge contractType={item.contractType} />
        </span>
        <StatusPill row={item} />
      </div>

      <div className='d-flex align-items-center gap-2'>
        <ProductAvatar name={item.productType?.product?.name} />
        <div className='pv-text-col flex-grow-1'>
          <div className='to-title'>{item.titleProcurement || '-'}</div>
          <ProductInfo item={item} />
        </div>
      </div>

      <div className='to-mcard-meta'>
        <div>
          <span>Proc Type</span>
          <span>{PROCUREMENT_TYPE_MAP[item.procurementType] || '-'}</span>
        </div>
        <div>
          <span>Created</span>
          <span>{formatDateToDDMMYYYY(item.createdDate) || '-'}</span>
        </div>
      </div>

      <div className='d-flex justify-content-between align-items-center gap-2 mb-3'>
        <DeadlineChip
          deadline={item.proposalSubmissionDeadline}
          isClosed={item.isProcurementCompleted}
        />
        <LastUpdate row={item} />
      </div>

      {item.description && (
        <p className='pv-desc mb-3' title={item.description}>
          {item.description}
        </p>
      )}

      <div className='mb-3'>
        <CompletionSteps item={item} />
      </div>

      <div className='d-flex gap-2'>
        <ActionButton
          variant='secondary'
          size='sm'
          icon='ri-eye-line'
          className='flex-fill'
          onClick={() => goToDetail(item)}
        >
          View Detail
        </ActionButton>
        {!item.isProcurementCompleted && (
          <ActionButton
            variant='destructive'
            size='sm'
            icon='ri-delete-bin-line'
            confirm={false}
            onClick={() => confirmDelete(item)}
          >
            Delete
          </ActionButton>
        )}
      </div>
    </div>
  );

  return (
    <>
      <Row className='g-3 mb-3'>
        {STAT_CARDS.map(card => (
          <Col key={card.key} xs={12} sm={4}>
            <StatCard
              card={card}
              summary={summary}
              isActive={completedFilter === card.filter}
              onSelect={() =>
                handleStatusFilter(
                  completedFilter === card.filter && card.filter
                    ? ''
                    : card.filter
                )
              }
            />
          </Col>
        ))}
      </Row>

      <div className='to-panel'>
        <div className='to-toolbar'>
          <ListSearch
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder='Search by title or procurement no...'
            ariaLabel='Search procurement'
          />
          <ListFilterSelect
            label='Status'
            value={completedFilter}
            onChange={handleStatusFilter}
            allLabel='All Status'
            options={STATUS_OPTIONS}
          />
          <ListFilterSelect
            label='Proc Type'
            value={procurementTypeFilter}
            onChange={handleProcurementTypeFilter}
            allLabel='All Types'
            options={PROCUREMENT_TYPE_OPTIONS}
          />
          <ListFilterSelect
            label='Period'
            value={contractTypeFilter}
            onChange={handleContractTypeFilter}
            allLabel='All Periods'
            options={CONTRACT_TYPE_OPTIONS}
          />
          <ClearFiltersButton
            count={filterChips.length}
            onClick={clearFilters}
          />
          <span className='to-toolbar-meta'>
            {isLoading
              ? 'Loading...'
              : `Showing ${startEntry}–${endEntry} of ${totalRows} procurements`}
          </span>
          <UncontrolledDropdown>
            <DropdownToggle
              tag='button'
              type='button'
              className='btn btn-light btn-sm d-inline-flex align-items-center'
              disabled={isExporting || isLoading || !totalRows}
            >
              {isExporting ? (
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                  aria-hidden='true'
                />
              ) : (
                <i className='ri-download-2-line me-2' aria-hidden='true' />
              )}
              Export
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={() => handleExport('xlsx')}>
                <i className='ri-file-excel-2-line align-middle me-2 text-success'></i>
                Export to Excel
              </DropdownItem>
              <DropdownItem onClick={() => handleExport('csv')}>
                <i className='ri-file-text-line align-middle me-2 text-muted'></i>
                Export to CSV
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>

        <ActiveFilterChips chips={filterChips} />

        <div className='to-legend d-none d-md-flex'>
          <span className='fw-medium'>Completion steps:</span>
          {STEP_DEFS.map(step => (
            <span key={step.key} className='to-legend-item'>
              <i className={`${step.icon} me-2`} aria-hidden='true' />
              {step.label}
            </span>
          ))}
        </div>

        <div className='d-md-none p-3'>
          {isLoading && <SkeletonRows count={3} />}
          {!isLoading &&
            items.map((item, index) => (
              <MobileCard key={item.id || index} item={item} />
            ))}
          {!isLoading && !items.length && emptyState}
        </div>

        <div className='d-none d-md-block to-table-wrap'>
          <DataTable
            columns={columns}
            data={items}
            pagination={false}
            onSort={handleSort}
            sortServer
            onRowClicked={goToDetail}
            conditionalRowStyles={READY_ROW_STYLES}
            highlightOnHover
            pointerOnHover
            persistTableHead
            progressPending={isLoading}
            progressComponent={<SkeletonRows />}
            noDataComponent={emptyState}
            customStyles={{
              headCells: {
                style: { paddingLeft: '12px', paddingRight: '12px' },
              },
              cells: {
                style: {
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  wordBreak: 'break-word',
                },
              },
              table: { style: { width: '100%' } },
              tableWrapper: { style: { display: 'table' } },
            }}
          />
        </div>

        <div className='to-footer'>
          <div className='d-flex align-items-center gap-2'>
            <span>Rows per page</span>
            <select
              className='form-select form-select-sm'
              style={{ width: 'auto' }}
              value={rowsPerPage}
              onChange={e => handlePerRowsChange(parseInt(e.target.value, 10))}
              aria-label='Rows per page'
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className='to-footer-range'>
              {startEntry}–{endEntry} of <strong>{totalRows}</strong> items
            </span>
          </div>

          <div className='to-pager'>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage <= 1 || isLoading}
              onClick={() => setCurrentPage(1)}
              aria-label='First page'
              title='First page'
            >
              <i className='ri-skip-back-line' aria-hidden='true' />
            </button>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage <= 1 || isLoading}
              onClick={() => setCurrentPage(currentPage - 1)}
              aria-label='Previous page'
              title='Previous page'
            >
              <i className='ri-arrow-left-s-line' aria-hidden='true' />
            </button>
            <span className='to-pager-current'>
              Page {currentPage} of {totalPages}
            </span>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage >= totalPages || isLoading}
              onClick={() => setCurrentPage(currentPage + 1)}
              aria-label='Next page'
              title='Next page'
            >
              <i className='ri-arrow-right-s-line' aria-hidden='true' />
            </button>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage >= totalPages || isLoading}
              onClick={() => setCurrentPage(totalPages)}
              aria-label='Last page'
              title='Last page'
            >
              <i className='ri-skip-forward-line' aria-hidden='true' />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default React.memo(ProcurementTable);
